import React, { useEffect, useMemo, useState } from 'react';
import api from '@mysimpledb/shared/config/axios';
import './PhoneNumberField.css';
import {
  isoToFlag,
  formatAsYouType,
  normalizePhoneForSave,
  formatPhoneForDisplay,
} from './phoneUtils';

export { normalizePhoneForSave, formatPhoneForDisplay };

export default function PhoneNumberField({
  countryValue,
  onCountryChange,
  phoneValue,
  onPhoneChange,
  disabled = false,
  label = 'Phone Number',
  required = false,
  className = '',
}) {
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [displayPhone, setDisplayPhone] = useState('');

  useEffect(() => {
    let ignore = false;

    async function loadCountries() {
      try {
        const res = await api.get('/countries');
        if (!ignore) {
          setCountries(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        console.error('Failed to load countries:', err);
      } finally {
        if (!ignore) {
          setLoadingCountries(false);
        }
      }
    }

    loadCountries();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!phoneValue) {
      setDisplayPhone('');
      return;
    }

    if (phoneValue.startsWith('+')) {
      setDisplayPhone(formatPhoneForDisplay(phoneValue, countryValue));
    } else {
      setDisplayPhone(phoneValue);
    }
  }, [phoneValue, countryValue]);

  const sortedCountries = useMemo(() => {
    return [...countries].sort((a, b) => a.name.localeCompare(b.name));
  }, [countries]);

  const selectedCountry =
    sortedCountries.find((c) => c.iso_code === countryValue) || null;

  const handleCountryChange = (e) => {
    const nextCountry = e.target.value;
    onCountryChange?.(nextCountry);

    const reformatted = formatAsYouType(displayPhone, nextCountry);
    setDisplayPhone(reformatted);
    onPhoneChange?.(reformatted);
  };

  const handlePhoneChange = (e) => {
    const rawInput = e.target.value;
    const formatted = formatAsYouType(rawInput, countryValue || 'US');
    setDisplayPhone(formatted);
    onPhoneChange?.(formatted);
  };

  const placeholder = selectedCountry
    ? `Phone number (${selectedCountry.dial_code})`
    : 'Phone number';

  return (
    <div className={`phone-number-field ${className}`.trim()}>
      <label className="phone-number-field__label">
        {label}
        {required ? ' *' : ''}
      </label>

      <div className="phone-number-field__row">
        <select
          className="phone-number-field__country standard-input"
          value={countryValue || ''}
          onChange={handleCountryChange}
          disabled={disabled || loadingCountries}
          aria-label="Phone country"
        >
          <option value="" disabled>
            {loadingCountries ? 'Loading...' : 'Select country'}
          </option>

          {sortedCountries.map((country) => (
            <option key={country.iso_code} value={country.iso_code}>
              {`${isoToFlag(country.iso_code)} ${country.iso_code} (${
                country.dial_code
              })`}
            </option>
          ))}
        </select>

        <input
          className="phone-number-field__input standard-input"
          type="tel"
          value={displayPhone}
          onChange={handlePhoneChange}
          autoComplete="tel-national"
          disabled={disabled}
          placeholder={placeholder}
          aria-label="Phone number"
        />
      </div>
    </div>
  );
}
